import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

const API_BASE = "/api/persons";

const emptyForm = {
  tipo: "N",
  cedula: "",
  ruc: "",
  razon_social: "",
  nombre_comercial: "",
  telefono: "",
  correo: "",
  direccion: "",
  es_cliente: true,
  es_proveedor: false,
  es_empleado: false,
  es_extranjero: false,
};

function getToken() {
  return localStorage.getItem("accessToken") || localStorage.getItem("access") || "";
}

export default function Persons({
  open,
  person = null,
  onClose,
  onSaved,
  initialValues,
}) {
  const { dashboardTheme } = useDashboardTheme();
  const { showSuccess, showError, showInfo } = useAlert();

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const token = getToken();
  const isEdit = Boolean(person);

  useEffect(() => {
    if (!open) return;

    if (person) {
      setForm({
        tipo: person.tipo || "N",
        cedula: person.cedula || "",
        ruc: person.ruc || "",
        razon_social: person.razon_social || "",
        nombre_comercial: person.nombre_comercial || "",
        telefono: person.telefono || "",
        correo: person.correo || "",
        direccion: person.direccion || "",
        es_cliente: Boolean(person.es_cliente),
        es_proveedor: Boolean(person.es_proveedor),
        es_empleado: Boolean(person.es_empleado),
        es_extranjero: Boolean(person.es_extranjero),
      });
      return;
    }

    setForm({
      ...emptyForm,
      ...(initialValues || {}),
      tipo: initialValues?.tipo || "N",
      es_cliente:
        typeof initialValues?.es_cliente === "boolean"
          ? initialValues.es_cliente
          : true,
      es_proveedor:
        typeof initialValues?.es_proveedor === "boolean"
          ? initialValues.es_proveedor
          : false,
      es_empleado:
        typeof initialValues?.es_empleado === "boolean"
          ? initialValues.es_empleado
          : false,
      es_extranjero:
        typeof initialValues?.es_extranjero === "boolean"
          ? initialValues.es_extranjero
          : false,
    });
  }, [open, person?.id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const closeMainModal = () => {
    if (saving) return;
    onClose?.();
  };

  const parseBackendError = (data) => {
    const error = data?.error || data?.detail;

    if (!error) {
      return "No se pudo guardar la persona.";
    }

    if (typeof error === "string") {
      return error;
    }

    if (typeof error === "object") {
      return Object.values(error).flat().join(" ");
    }

    return "No se pudo guardar la persona.";
  };

  const validateForm = () => {
    if (!form.razon_social.trim()) {
      showInfo("La razón social es obligatoria.");
      return false;
    }

    if (
      !form.es_cliente &&
      !form.es_proveedor &&
      !form.es_empleado &&
      !form.es_extranjero
    ) {
      showInfo("Selecciona al menos un rol para la persona.");
      return false;
    }

    return true;
  };

  const savePerson = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const response = await fetch(isEdit ? `${API_BASE}/${person.id}/` : `${API_BASE}/crear/`, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tipo: form.tipo || "N",
          cedula: form.cedula,
          ruc: form.ruc,
          razon_social: form.razon_social,
          nombre_comercial: form.nombre_comercial,
          telefono: form.telefono,
          correo: form.correo,
          direccion: form.direccion,
          es_cliente: Boolean(form.es_cliente),
          es_proveedor: Boolean(form.es_proveedor),
          es_empleado: Boolean(form.es_empleado),
          es_extranjero: Boolean(form.es_extranjero),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showError(parseBackendError(data));
        return;
      }

      showSuccess(
        data.mensaje ||
          (isEdit ? "Persona actualizada correctamente." : "Persona creada correctamente.")
      );

      onSaved?.(data);
    } catch {
      showError("Error de conexión.");
    } finally {
      setSaving(false);
    }
  };

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const roleLabelSx = {
    "& .MuiFormControlLabel-label": {
      color: dashboardTheme.text_color,
      fontSize: 14,
      fontWeight: 500,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={closeMainModal}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: dashboardTheme.card_background,
          border: `1px solid ${dashboardTheme.border_color}`,
          boxShadow: "none",
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        <Stack spacing={2.5}>
          <Typography
            sx={{
              color: dashboardTheme.primary_color,
              fontSize: { xs: 22, md: 26 },
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            {isEdit ? "Editar persona" : "Nueva Persona"}
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl fullWidth size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Tipo de persona</InputLabel>
              <Select
                label="Tipo de persona"
                value={form.tipo}
                onChange={(event) => handleChange("tipo", event.target.value)}
              >
                <MenuItem value="N">Natural</MenuItem>
                <MenuItem value="J">Jurídica</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Cédula"
              value={form.cedula}
              onChange={(event) => handleChange("cedula", event.target.value)}
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="RUC"
              value={form.ruc}
              onChange={(event) => handleChange("ruc", event.target.value)}
              sx={dashboardTheme.inputStyles}
            />
          </Stack>

          <TextField
            fullWidth
            size="small"
            label="Razón social"
            value={form.razon_social}
            onChange={(event) => handleChange("razon_social", event.target.value)}
            sx={dashboardTheme.inputStyles}
          />

          <TextField
            fullWidth
            size="small"
            label="Nombre comercial"
            value={form.nombre_comercial}
            onChange={(event) => handleChange("nombre_comercial", event.target.value)}
            sx={dashboardTheme.inputStyles}
          />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Teléfono"
              value={form.telefono}
              onChange={(event) => handleChange("telefono", event.target.value)}
              sx={dashboardTheme.inputStyles}
            />

            <TextField
              fullWidth
              size="small"
              label="Correo"
              type="email"
              value={form.correo}
              onChange={(event) => handleChange("correo", event.target.value)}
              sx={dashboardTheme.inputStyles}
            />
          </Stack>

          <TextField
            fullWidth
            size="small"
            label="Dirección"
            multiline
            minRows={2}
            value={form.direccion}
            onChange={(event) => handleChange("direccion", event.target.value)}
            sx={dashboardTheme.inputStyles}
          />

          <Box
            sx={{
              border: `1px solid ${dashboardTheme.border_color}`,
              bgcolor: dashboardTheme.dashboard_background,
              borderRadius: "12px",
              p: { xs: 1.5, md: 2 },
            }}
          >
            <Typography
              sx={{
                mb: 1,
                color: dashboardTheme.text_color,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Roles
            </Typography>

            <FormGroup
              row
              sx={{
                gap: { xs: 0.5, md: 1.5 },
              }}
            >
              <FormControlLabel
                sx={roleLabelSx}
                control={
                  <Checkbox
                    checked={form.es_cliente}
                    onChange={(event) => handleChange("es_cliente", event.target.checked)}
                    sx={{
                      color: dashboardTheme.button_color,
                      "&.Mui-checked": {
                        color: dashboardTheme.button_color,
                      },
                    }}
                  />
                }
                label="Cliente"
              />

              <FormControlLabel
                sx={roleLabelSx}
                control={
                  <Checkbox
                    checked={form.es_proveedor}
                    onChange={(event) => handleChange("es_proveedor", event.target.checked)}
                    sx={{
                      color: dashboardTheme.button_color,
                      "&.Mui-checked": {
                        color: dashboardTheme.button_color,
                      },
                    }}
                  />
                }
                label="Proveedor"
              />

              <FormControlLabel
                sx={roleLabelSx}
                control={
                  <Checkbox
                    checked={form.es_empleado}
                    onChange={(event) => handleChange("es_empleado", event.target.checked)}
                    sx={{
                      color: dashboardTheme.button_color,
                      "&.Mui-checked": {
                        color: dashboardTheme.button_color,
                      },
                    }}
                  />
                }
                label="Empleado"
              />

              <FormControlLabel
                sx={roleLabelSx}
                control={
                  <Checkbox
                    checked={form.es_extranjero}
                    onChange={(event) => handleChange("es_extranjero", event.target.checked)}
                    sx={{
                      color: dashboardTheme.button_color,
                      "&.Mui-checked": {
                        color: dashboardTheme.button_color,
                      },
                    }}
                  />
                }
                label="Extranjero"
              />
            </FormGroup>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              pt: 0.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={closeMainModal}
              disabled={saving}
              sx={outlinedButtonSx}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={savePerson}
              disabled={saving}
              sx={dashboardTheme.buttonStyles.contained}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}